import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import useWindowDimensions from '../services/useWindowDimensions';

/** Full-width operational workspaces use an overlay menu on narrow screens. */
export function WorkspaceLayout({
  navigation,
  children,
}: {
  navigation: ReactNode;
  children: ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const { width } = useWindowDimensions();
  const { pathname } = useLocation();
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (menuOpen) dialog.current?.showModal();
    else dialog.current?.close();
  }, [menuOpen]);
  useEffect(() => {
    if (width >= 1024) setMenuOpen(false);
  }, [width]);
  return (
    <div className="flex min-w-0 flex-1 bg-secondary">
      <div className="hidden shrink-0 lg:flex">{navigation}</div>
      <dialog
        ref={dialog}
        aria-label="Application navigation"
        onCancel={() => setMenuOpen(false)}
        onClose={() => setMenuOpen(false)}
        className="fixed inset-0 m-0 h-[100dvh] max-h-none w-screen max-w-none bg-transparent p-0 backdrop:bg-black/40"
      >
        {menuOpen && (
          <div className="flex h-full">
            <button
              aria-label="Dismiss navigation backdrop"
              tabIndex={-1}
              className="absolute inset-0"
              onClick={() => setMenuOpen(false)}
            />
            <div
              className="relative flex h-full"
              role="navigation"
              aria-label="Application navigation"
            >
              {navigation}
            </div>
            <button
              className="absolute right-3 top-3 rounded border bg-white p-3 text-sm"
              onClick={() => setMenuOpen(false)}
              autoFocus
            >
              Close navigation
            </button>
          </div>
        )}
      </dialog>
      <div className="max-h-screen min-w-0 flex-1 overflow-auto p-2 md:p-4 lg:p-8">
        <button
          className="mb-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm lg:hidden"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          Open navigation
        </button>
        {children}
      </div>
    </div>
  );
}
